'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { graphql } from '~/gql'
import { useGQLQuery, useGQLSuspenseQuery } from '~/hooks/useGraphQL'
import { gqlClient } from '~/hooks/useGraphQL.server'
import { useCallback, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  CreateEvaluation_EvaluationFragment,
  DashboardEvaluationsQuery,
} from '~/gql/graphql'
import { set } from 'lodash/fp'

// 10 Minutes
const staleTime = 10 * 60 * 1000

const dashboardWorkspaceQuery = graphql(`
  query WorkspaceDashboard {
    dashboard {
      owner {
        memberId
      }
      workspace {
        id
        name
        parentId
        creatorId
        commonName
        billingCode
        showBillingCode
        orgName
        members {
          roles
          user {
            memberId
            profile {
              email
            }
          }
        }
      }
    }
  }
`)

export const useDashboardWorkspaceQuery = (enabled: boolean = true) => {
  const dashboardQueryResult = useGQLQuery(dashboardWorkspaceQuery, null, {
    staleTime,
    enabled,
  })
  
  const loadSessionIdRef = useRef<string | null>(null)
  const hasRecordedStartRef = useRef(false)

  // Record dashboard load start when query is enabled - trigger once when enabled becomes true
  useEffect(() => {
    // Simple condition: if enabled and we haven't recorded yet, record it
    // This will trigger once when the query is first enabled
    if (enabled && !hasRecordedStartRef.current) {
      hasRecordedStartRef.current = true // Mark as recorded immediately to prevent duplicates
      
      // Generate a session ID on the frontend to use as a key for matching start/end events
      // This is especially important when workspaceId/memberId are null
      const frontendSessionId = uuidv4()
      loadSessionIdRef.current = frontendSessionId
      
      // Record load start event (server-side GraphQL call)
      import('./performance-metrics').then(({ recordDashboardLoadStart }) => {
        console.warn('[Dashboard Load] ⚠️ Import successful, calling recordDashboardLoadStart')
        recordDashboardLoadStart({
          sessionId: frontendSessionId, // Send frontend-generated session ID
          workspaceId: null, // Will be set when we have workspace data
          memberId: null, // Will be set when we have memberId from context
        })
          .then((result: any) => {
            const data = result?.recordDashboardLoadStart
            if (data?.success && data?.sessionId) {
              loadSessionIdRef.current = data.sessionId
            } else {
              // Reset flag on failure so we can retry
              hasRecordedStartRef.current = false
            }
          })
          .catch((error) => {
            console.error('[Dashboard Load] ❌ Failed to record start event:', error)
            // Reset flag on error so we can retry
            hasRecordedStartRef.current = false
          })
      }).catch((error) => {
        console.error('[Dashboard Load] ❌ Failed to import performance-metrics:', error)
        hasRecordedStartRef.current = false
      })
    }
  }, [enabled, dashboardQueryResult.isLoading, dashboardQueryResult.fetchStatus, dashboardQueryResult.status]) // Add more dependencies to ensure it runs

  useEffect(() => {
    if (dashboardQueryResult.isSuccess) {
      // Check if window.analytics is available
      if ((window as any).analytics) {
        // Track the 'dashboard_request' event
        ;(window as any).analytics.track('dashboard_request')
      }
    }
  }, [dashboardQueryResult.isSuccess])

  // Store session ID in the result for use in dashboardContext
  return {
    ...dashboardQueryResult,
    loadSessionId: loadSessionIdRef.current,
    loadSessionIdRef,
  }
}

const dashboardEvaluationsQuery = graphql(`
  query DashboardEvaluations {
    dashboard {
      owner {
        memberId
      }
      workspace {
        id
      }
      evaluations {
        id
        name
        workspace {
          id
          creatorId
        }
        versionNumber
        modDate
        createDate
        slug
        status {
          key
          value
        }
        type
        collaborators {
          id
          avatar
          name
          email
          allowPermissions
          roles
        }
        owner {
          auth0Id
          name
          memberId
          profile {
            fullName
            avatar
            email
          }
        }
        evaluationVersions {
          id
          evaluationId
          versionNumber
        }
      }
    }
  }
`)

export const useDashboardEvaluationsQuery = (enabled: boolean = true) => {
  const dashboardQueryResult = useGQLQuery(
    dashboardEvaluationsQuery,
    null,
    // Set stale time as 10 minutes so the evaluations aren't fetch too often
    { staleTime, enabled }
  )

  return dashboardQueryResult
}

export const useInvalidateDashboardEvaluationsQuery = () => {
  const queryClient = useQueryClient()

  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['DashboardEvaluations', undefined],
    })
  }, [queryClient])
}

export function useAddEvaluationToDashboardList() {
  const queryClient = useQueryClient()
  return useCallback((newEvaluation: CreateEvaluation_EvaluationFragment) => {
    queryClient.setQueryData<DashboardEvaluationsQuery>(
      ['DashboardEvaluations', undefined],
      (currentData: DashboardEvaluationsQuery) => {
        if (!currentData) return
        // Filter out evaluations with the same evaluationId.
        // Scenario where there's a new version being added and we want to get rid of old
        // versions of the same evaluation
        const evaluationsFiltered = currentData?.dashboard?.evaluations.filter(
          (evaluation) =>
            evaluation.evaluationVersions?.[0]?.evaluationId !==
            newEvaluation.evaluationVersions?.[0]?.evaluationId
        )
        const updatedEvaluations = [newEvaluation, ...evaluationsFiltered]

        return set(`dashboard.evaluations`, updatedEvaluations, currentData)
      }
    )
  }, [])
}

const workspaceListQuery = graphql(`
  query WorkspacList($memberID: String!) {
    getWorkspacesForMember(memberId: $memberID) {
      id
      name
      parentId
      creatorId
      commonName
      billingCode
      showBillingCode
      orgName
    }
  }
`)

export const useWorkspaceList = (memberID: string) => {
  // Log if system user ID is being used (should never happen)
  const SYSTEM_USER_ID = '409b6857-a544-4aa2-a3f1-9747be7958d5'
  if (memberID === SYSTEM_USER_ID) {
    console.error(
      `[CRITICAL] useWorkspaceList called with system user ID: ${memberID}. ` +
      `This should never happen. Check if memberId is being set incorrectly.`
    )
  }
  
  // Use regular query instead of suspense query to avoid issues when enabled is false
  // Suspense queries can cause problems when disabled, especially in production
  const queryResult = useGQLQuery(
    workspaceListQuery,
    memberID ? { memberID } : null,
    { enabled: !!memberID, staleTime }
  )

  const retryCountRef = useRef(0)
  const hasReloadedRef = useRef(false)
  const isFirstAttemptRef = useRef(true)
  const maxRetries = 4
  const retryDelayMs = 2000

  useEffect(() => {
    // Skip if already reloaded or no memberID
    if (!memberID || hasReloadedRef.current) {
      return
    }

    // Only process when query is successful and has data
    if (!queryResult.isSuccess || !queryResult.data) {
      return
    }

    const workspaces = queryResult.data?.getWorkspacesForMember
    const hasWorkspaces = workspaces && workspaces.length > 0
    const hasNoWorkspaces = workspaces && workspaces.length === 0

    // If workspaces found on first attempt, continue normally (no reload needed)
    if (hasWorkspaces && isFirstAttemptRef.current) {
      isFirstAttemptRef.current = false
      return
    }

    // If workspaces found after a retry, reload to rerun all GraphQL queries
    if (hasWorkspaces && !isFirstAttemptRef.current && !hasReloadedRef.current) {
      hasReloadedRef.current = true
      console.log(
        `[WorkspaceList] Workspaces found after retry. Reloading dashboard to rerun all GraphQL queries...`
      )
      window.location.reload()
      return
    }

    // If no workspaces and we haven't exhausted retries, schedule a retry
    if (hasNoWorkspaces && retryCountRef.current < maxRetries) {
      // Mark that we're past the first attempt
      isFirstAttemptRef.current = false
      
      // Increment retry count immediately to prevent multiple retries from being scheduled
      const currentRetry = retryCountRef.current + 1
      retryCountRef.current = currentRetry
      
      const retryTimer = setTimeout(async () => {
        console.log(
          `[WorkspaceList] No workspaces found, retrying (${currentRetry}/${maxRetries})...`
        )
        await queryResult.refetch()
      }, retryDelayMs)

      return () => clearTimeout(retryTimer)
    }

    // If we've exhausted retries and still have no workspaces, reload the page
    // This is critical because all dashboard queries depend on workspaces being available
    if (hasNoWorkspaces && retryCountRef.current >= maxRetries && !hasReloadedRef.current) {
      hasReloadedRef.current = true
      console.warn(
        `[WorkspaceList] No workspaces found after ${maxRetries} retries. ` +
        `All dashboard queries depend on workspaces, so reloading the entire page...`
      )
      // Reload the entire page to reset all queries that depend on workspaces
      window.location.reload()
    }
  }, [
    queryResult.isSuccess,
    queryResult.data?.getWorkspacesForMember,
    memberID,
    queryResult.refetch,
  ])

  // Reset retry count, reload flag, and first attempt flag when memberID changes
  useEffect(() => {
    retryCountRef.current = 0
    hasReloadedRef.current = false
    isFirstAttemptRef.current = true
  }, [memberID])

  return queryResult
}

const changeDashboardMutation = graphql(`
  mutation changeDashboardMutation($workspaceId: String!) {
    changeDashboard(workspaceId: $workspaceId) {
      id
    }
  }
`)

export const useChangeDashboardMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (workspaceId: string) =>
      gqlClient().request(changeDashboardMutation, { workspaceId }),
    async onSuccess() {
      const keys = [
        ['DashboardEvaluations'] as const,
        ['WorkspaceDashboard'] as const,
        ['WorkspacList'] as const,
        ['MembersList'] as const,
      ]

      await Promise.all(
        keys.map((queryKey) => queryClient.cancelQueries({ queryKey }))
      )

      await Promise.all(
        keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
      )
    },
  })
}
